import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

export function getUserDisplayName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim()
}

/**
 * Parse an ISO day key (YYYY-MM-DD) as a LOCAL date.
 * `new Date("2026-05-29")` parses as UTC midnight, which renders as the
 * previous day in any negative-offset timezone (e.g. US Pacific). Splitting
 * the parts and using the local Date constructor keeps the calendar day stable.
 */
function parseLocalDate(isoDate: string): Date {
  const parts = isoDate.split("-").map(Number)
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [year, month, day] = parts
    return new Date(year, month - 1, day)
  }
  return new Date(isoDate)
}

export function formatWorkoutDate(isoDate: string): string {
  try {
    const date = parseLocalDate(isoDate)
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" })
    const month = date.toLocaleDateString("en-US", { month: "short" })
    const day = date.getDate()
    return `${dayName}, ${month} ${day}`
  } catch {
    return isoDate
  }
}

export function sortDates(dates: string[]): string[] {
  return [...dates].sort(
    (a, b) => parseLocalDate(a).getTime() - parseLocalDate(b).getTime(),
  )
}

export function getDayName(isoDate: string): string {
  try {
    return parseLocalDate(isoDate).toLocaleDateString("en-US", { weekday: "long" })
  } catch {
    return "Unknown"
  }
}

export function formatDateRange(days: Record<string, unknown>): string {
  const dates = sortDates(Object.keys(days))
  if (dates.length === 0) return ""

  const firstDate = parseLocalDate(dates[0])
  const lastDate = parseLocalDate(dates[dates.length - 1])

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric" })

  return `${formatDate(firstDate)} - ${formatDate(lastDate)}`
}

/**
 * A person's label, formatted as a phone number when a phone number is all it is.
 *
 * `member_names` (backend) resolves a member to their typed name, then their
 * waitlist name, then — for anyone who has never given us a name on either
 * side — the raw phone column. That last group is everyone who reaches us over
 * iMessage, and a bare `3108673373` sitting next to "Kelsey Shinn" reads like a
 * rendering bug rather than a person. Formatted, it reads like someone you can
 * text, and the digits still line up against the waitlist by eye.
 *
 * Anything containing a letter is a name and is returned untouched.
 */
export function nameOrPhone(label: string | null | undefined): string {
  const s = (label ?? "").trim()
  if (!s || /[a-z]/i.test(s)) return s
  const digits = s.replace(/\D/g, "")
  if (digits.length < 10 || digits.length > 15) return s
  const n = digits.length === 10 ? "1" + digits : digits
  // US/Canada only: everywhere else groups digits differently, and a wrong
  // grouping is harder to read than none.
  return n.length === 11 && n.startsWith("1")
    ? `(${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}`
    : "+" + n
}

export function phoneMatches(phone: string | null | undefined, query: string): boolean {
  if (/[a-z]/i.test(query)) return false
  const nationalDigits = (s: string) => {
    const d = s.replace(/\D/g, "")
    return d.length === 11 && d.startsWith("1") ? d.slice(1) : d
  }
  const q = nationalDigits(query)
  return q.length >= 3 && nationalDigits(phone ?? "").includes(q)
}
